(() => {
  if (window.__loreProductVideoLoad) return;
  window.__loreProductVideoLoad = true;
  const states = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const active = video => {
    const slide = video.closest('slideshow-slide');
    return !slide || (!slide.hidden && slide.getAttribute('aria-hidden') !== 'true');
  };
  const selector = 'main video.lore-story-video, main deferred-media[data-lore-story-media] video';
  const scope = video => video.closest('deferred-media[data-lore-story-media], slideshow-slide, .hero');
  function controls(video) {
    const root = scope(video);
    if (!root) return;
    const media = video.closest('deferred-media[data-lore-story-media]');
    if (media) {
      media.isPlaying = !video.paused;
      media.updatePlayPauseHint?.(!video.paused);
      const toggle = media.querySelector('[ref="toggleMediaButton"]');
      if (toggle) toggle.setAttribute('aria-label', video.paused ? media.dataset.playLabel : media.dataset.pauseLabel);
    }
    root.querySelectorAll('a[href="#play"]').forEach(button => {
      button.setAttribute('role', 'button');
      button.dataset.lorePlayback = video.paused ? 'paused' : 'playing';
      button.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    });
    root.querySelectorAll('a[href="#mute"]').forEach(button => {
      button.setAttribute('role', 'button');
      button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    });
  }
  function sync(video, state) {
    const shouldPlay = state.visible && active(video) && !document.hidden &&
      !state.userPaused && (state.autoplay || state.userStarted) && (!motion.matches || state.userStarted);
    if (shouldPlay) {
      if (video.preload === 'none') video.preload = 'metadata';
      if (video.paused && !state.pending) {
        const request = {};
        state.pending = request;
        video.play().catch(() => {}).finally(() => {
          if (state.pending === request) state.pending = null;
          controls(video);
        });
      }
    } else {
      state.pending = null;
      if (!video.paused) { state.internalPause = true; video.pause(); }
    }
    controls(video);
  }
  const near = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(({target: video, isIntersecting}) => {
      if (isIntersecting && active(video) && video.preload === 'none') video.preload = 'metadata';
    });
  }, {rootMargin: '250px 0px'}) : null;
  const viewport = near ? new IntersectionObserver(entries => {
    entries.forEach(({target: video, isIntersecting}) => {
      const state = states.get(video);
      if (state) { state.visible = isIntersecting; sync(video, state); }
    });
  }, {threshold: 0.01}) : null;
  function refresh() {
    for (const [video] of states) {
      if (!video.isConnected) {
        video.pause();
        near?.unobserve(video);
        viewport?.unobserve(video);
        states.delete(video);
      }
    }
    document.querySelectorAll(selector).forEach(video => {
      if (states.has(video)) return;
      const media = video.closest('deferred-media[data-lore-story-media]');
      const userStarted = Boolean(media?.hasAttribute('data-media-loaded'));
      const rect = userStarted ? video.getBoundingClientRect() : null;
      const visible = !viewport || Boolean(rect && rect.bottom > 0 && rect.top < innerHeight);
      const state = {visible, userPaused: false, userStarted, pending: false, internalPause: false, autoplay: !media || media.hasAttribute('autoplay')};
      if (!media && !scope(video)?.querySelector('a[href="#play"]')) video.controls = true;
      states.set(video, state);
      ['play', 'pause', 'ended', 'volumechange'].forEach(type => video.addEventListener(type, () => {
        if (type === 'play') {
          if ((media || video.controls) && !state.pending) { state.userStarted = true; state.userPaused = false; }
          if (!state.visible || !active(video) || document.hidden || state.userPaused || (motion.matches && !state.userStarted)) {
            state.internalPause = true;
            video.pause();
          }
        }
        if (type === 'pause') {
          if (state.internalPause) state.internalPause = false;
          else if (media || video.controls) { state.userPaused = true; state.pending = null; }
        }
        controls(video);
      }));
      near?.observe(video);
      viewport?.observe(video);
    });
    states.forEach((state, video) => sync(video, state));
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.('a[href="#play"], a[href="#mute"]');
    if (!button) return;
    const root = button.closest('slideshow-slide, .hero');
    const videos = root ? [...root.querySelectorAll('video')].filter(video => states.has(video)) : [];
    if (!videos.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.getAttribute('href') === '#mute') {
      const muted = videos.some(video => !video.muted);
      videos.forEach(video => { video.muted = muted; controls(video); });
      return;
    }
    const pause = videos.some(video => !video.paused);
    videos.forEach(video => {
      const state = states.get(video);
      if (!state) return;
      state.userPaused = pause;
      state.userStarted = !pause;
      if (!pause) state.visible = true;
      sync(video, state);
    });
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== ' ' || event.repeat) return;
    const button = event.target.closest?.('a[href="#play"], a[href="#mute"]');
    const root = button?.closest('slideshow-slide, .hero');
    if (!root || ![...root.querySelectorAll('video')].some(video => states.has(video))) return;
    event.preventDefault();
    button.click();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, {once: true});
  else refresh();
  ['slideshow:select', 'variant:update', 'shopify:section:load', 'shopify:section:unload'].forEach(type => document.addEventListener(type, refresh));
  document.addEventListener('visibilitychange', () => states.forEach((state, video) => sync(video, state)));
  motion.addEventListener('change', () => {
    states.forEach((state, video) => { state.userStarted = false; sync(video, state); });
  });
  const main = document.querySelector('main');
  if (main) new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
      node.nodeType === 1 && (node.matches?.('video, deferred-media[data-lore-story-media]') || node.querySelector?.('video, deferred-media[data-lore-story-media]'))))) refresh();
  }).observe(main, {childList: true, subtree: true});
})();
